import { EARTH_RADIUS } from 'constants/geolocation';
import { LatLng } from 'types/maps';

function toRadians(deg: number) {
  return (deg * Math.PI) / 180;
}

/**
 * Approximate spherical polygon area (in square meters) using
 * the Chamberlain & Duquette (2007) method:
 * area = |sum((lon2 - lon1) * (sin(lat1) + sin(lat2)))| * R^2 / 2
 * Works for polygons that don't cover a hemisphere. Good for farms.
 */
export function polygonAreaSqMeters(coords: LatLng[]) {
  if (!coords || coords.length < 3) return 0;
  let sum = 0;
  for (let i = 0; i < coords.length; i++) {
    const a = coords[i];
    const b = coords[(i + 1) % coords.length];
    const lat1 = toRadians(a.lat);
    const lat2 = toRadians(b.lat);
    const lon1 = toRadians(a.lng);
    const lon2 = toRadians(b.lng);
    sum += (lon2 - lon1) * (Math.sin(lat1) + Math.sin(lat2));
  }
  const area = (Math.abs(sum) * (EARTH_RADIUS * EARTH_RADIUS)) / 2.0;
  return area; // in m^2
}

export function areaSqMetersToHectares(m2: number) {
  return m2 / 10000;
}

export function areaSqMetersToKm2(m2: number) {
  return m2 / 1_000_000;
}

/**
 * Validates a coordinate is within valid range
 */
export function isValidCoord(lat: number, lng: number): boolean {
  return (
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180
  );
}
/**
 * Removes duplicate consecutive points and ensures minimum polygon size.
 * Handles polygon closing: removes closing point if present (assuming implicit closure)
 * or validates that polygon has at least 3 UNIQUE vertices.
 */
export function cleanPolygon(coords: LatLng[]): LatLng[] {
  if (!coords || coords.length < 3) return [];

  const cleaned: LatLng[] = [];
  const tolerance = 0.000001;

  for (let i = 0; i < coords.length; i++) {
    const current = coords[i];
    const last = cleaned[cleaned.length - 1];

    // Skip if same as last point (with small tolerance)
    if (last) {
      const latDiff = Math.abs(current.lat - last.lat);
      const lngDiff = Math.abs(current.lng - last.lng);
      if (latDiff < tolerance && lngDiff < tolerance) {
        continue;
      }
    }

    cleaned.push(current);
  }

  // Check if polygon is closed (last point equals first point)
  // If so, remove the closing point since we'll handle closure implicitly
  if (cleaned.length > 3) {
    const first = cleaned[0];
    const last = cleaned[cleaned.length - 1];
    const latDiff = Math.abs(first.lat - last.lat);
    const lngDiff = Math.abs(first.lng - last.lng);

    if (latDiff < tolerance && lngDiff < tolerance) {
      cleaned.pop(); // Remove closing point
    }
  }

  // Ensure we have at least 3 unique vertices for a valid polygon
  return cleaned.length >= 3 ? cleaned : [];
}
